import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { AppConfigService } from '../../services/app-config.service';
import { AuthTokenService } from '../../shared/services/auth-token.service';

@Component({
  selector: 'app-france-connect',
  templateUrl: './france-connect.component.html'
})
/**
 * Gestion de l'authentification, via FC
+--------+                                   +--------+
|        |                                   |        |
|        |---------(1) AuthN Request-------->|        |
|        |                                   |        |
|        |  +--------+                       |        |
|        |  |        |                       |        |
|        |  |  End-  |<--(2) AuthN & AuthZ-->|        |
|        |  |  User  |                       |        |
|   RP   |  |        |                       |   OP   |
|        |  +--------+                       |        |
|        |                                   |        |
|        |<--------(3) AuthN Response--------|        |
|        |                                   |        |
|        |---------(4) UserInfo Request----->|        |
|        |                                   |        |
|        |<--------(5) UserInfo Response-----|        |
|        |                                   |        |
+--------+                                   +--------+
 */
export class FranceConnectComponent {
  private static COOKIE_STATE = 'france_connect_open_id_state';
  private static COOKIE_NONCE = 'france_connect_open_id_nonce';
  private static COOKIE_AUTHORIZATION_CODE = 'france_connect_open_id_authorization_code';
  private static COOKIE_AUTHORIZATION_TOKEN = 'france_connect_open_id_bearer_token';
  overFranceConnect = false;
  displayMireLogin = false;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private readonly authTokenService: AuthTokenService) {
  }

  /**
   * Envoie le scénario d'authentification ((1) AuthN Request)
   */
  protected authentification() {
    const clientId = AppConfigService.settings.franceConnect.openIdParameters.clientID;
    const redirectUri = AppConfigService.settings.franceConnect.openIdParameters.callbackURL;
    const fcUrl = AppConfigService.settings.franceConnect.fcURL
    const fcWS = AppConfigService.settings.franceConnect.openIdParameters.authorizationURL;
    const responseType = 'code';
    const state = this.genererOuRecupererState();
    const nonce = this.genererOuRecupererNonce();
    const url = fcUrl + '/' + fcWS +
      '?response_type=' + responseType +
      '&client_id=' + clientId +
      '&scope=' +  AppConfigService.settings.franceConnect.openIdParameters.scope +
      '&state=' + state +
      '&nonce=' + nonce +
      '&redirect_uri=' + redirectUri +
      '&acr_values=eidas1'; // Only France Connect+ support other values
    document.location.href = url;
  }

  /**
   * Enregistrement du code d'authentification
   */
  registerAuthCode(code: string, receivedState: string): boolean {
    const originalState = this.genererOuRecupererState();
    if (originalState === receivedState) {
      this.creerCookie(FranceConnectComponent.COOKIE_AUTHORIZATION_CODE, code);
      console.log('Auth OK : [' + code + ']');

      this.getAuthenticateToken(code)

      return true;
    } else {
      console.log('State reçu invalide [' + receivedState + '], attendu : [' + originalState + ']');
      return false;
    }
  }

  getAuthenticateToken(code) {
    const clientId = AppConfigService.settings.franceConnect.openIdParameters.clientID;

    const body = {
      grant_type: 'authorization_code',
      client_id: clientId,
      code: code,
      redirect_uri: AppConfigService.settings.franceConnect.openIdParameters.callbackURL
    };

    const heads = this.createAuthorizationHeader('json');

    return this.http.post<any>(`${AppConfigService.settings.urlServeur}rest/auth/connect/proxy/`, JSON.stringify(body),
      {
        headers: heads
      })
      .map(data => {
        this.creerCookie(FranceConnectComponent.COOKIE_AUTHORIZATION_TOKEN, data.token);
        return data;
      })
      .catch((error: any) => {
        console.error('Erreur lors de la récupération du token d\'authentification : ', error);
        // Échec de l'authentification (ex : 401 sans Set-Cookie ACCESS_TOKEN/REFRESH_TOKEN émis par
        this.authTokenService.clearLocalSession();

        return Observable.throw(error.error || 'Server error')
      }
      );
  }

  getUserInfo(token) {
    const fcUrl = AppConfigService.settings.franceConnect.fcURL
    const fcWS = AppConfigService.settings.franceConnect.openIdParameters.userInfoURL;
    const url = fcUrl + '/' + fcWS + '?schema=openid';

    const heads = this.createAuthorizationHeader('bearer', token);

    return this.http.get(url, {
      headers: heads
    })
      .catch((error: any) => Observable.throw(error.error || 'Server error'));
  }

  /**
   * Fait appel à la déconnexion france connect
   */
  public deconnexion() {
    const fcUrl = AppConfigService.settings.franceConnect.fcURL
    const fcWS = AppConfigService.settings.franceConnect.openIdParameters.logoutURL;
    const state = this.genererOuRecupererState();
    const url = fcUrl + '/' + fcWS +
      '?id_token_hint=' + this.recupererCookie(FranceConnectComponent.COOKIE_AUTHORIZATION_TOKEN) +
      '&state=' + state +
      '&post_logout_redirect_uri=' + AppConfigService.settings.franceConnect.openIdParameters.callbackURL;

    // dirty fix for france connect v2 upgrade witch add a state parameter at the end
    // of the redirect_uri, making edia have 2 state cookie and crash
    this.cookieService.delete(FranceConnectComponent.COOKIE_STATE);

    return url;
  }

  private genererHexaAleatoire(length: number) {
    let result = '';
    for (let i = 0; i < length; i++) {
      result += '0123456789abcdef'.charAt(Math.floor(Math.random() * 16));
    }
    return result;
  }

  isLegacyLogin(): boolean {
    const legacyLogin = this.recupererCookie('legacyLogin');

    if (legacyLogin && legacyLogin.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  getConnected(): boolean {
    return this.authTokenService.isConnected();
  }

  private genererOuRecupererState() {
    let value = this.recupererCookie(FranceConnectComponent.COOKIE_STATE);
    if (value == null || value.length === 0) {
      value = this.genererHexaAleatoire(AppConfigService.settings.franceConnect.securityLength);
      this.creerCookie(FranceConnectComponent.COOKIE_STATE, value);
    }

    return value;
  }

  private genererOuRecupererNonce() {
    let value = this.recupererCookie(FranceConnectComponent.COOKIE_NONCE);
    if (value == null || value.length === 0) {
      value = this.genererHexaAleatoire(AppConfigService.settings.franceConnect.securityLength);
      this.creerCookie(FranceConnectComponent.COOKIE_NONCE, value);
    }

    return value;
  }

  /**
   * Retourne, en fonction du paramètre type le header à utiliser pour la requête http.
   * Le cookie d'authentification (ACCESS_TOKEN) est ajouté automatiquement à toutes les
   * requêtes vers le backend par le CredentialsInterceptor.
   *
   * @param {any} type
   * @param {any} token
   * @returns {HttpHeaders}
   */
  createAuthorizationHeader(type = null, token = null): HttpHeaders {
    switch (type) {
      case 'json':
        return new HttpHeaders({ 'Content-Type': 'application/json' });
      case 'form-data':
        return new HttpHeaders({ 'Content-Type': 'text/plain' });
      case 'bearer':
        return new HttpHeaders({ 'Authorization': 'Bearer ' + token });
      default:
        return new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    }
  }

  public isLogged() {
    return false;
  }

  protected recupererCookie(name: string): string {
    return this.cookieService.get(name);
  }

  protected creerCookie(name: string, value: string) {
    // Path explicite ('/') pour rester cohérent avec les autres routes de l'application.
    this.cookieService.set(name, value, undefined, '/');
  }
}



// WEBPACK FOOTER //
// ./src/app/components/france-connect/france-connect.component.ts